import { PARTY_ORDER, type PartyId } from '../types';
import riksdagJson from './riksdag.json';

/**
 * Riksdag election results, the single source for every election comparison
 * in the app and on the web. `riksdag.json` is generated by
 * `scripts/fetch-election.mjs` from Valmyndigheten's final count; do not edit
 * it by hand. Shared with the web through the `@shared` alias.
 */

export type ElectionYear = '2026' | '2022';

export type PartyShares = Record<PartyId, number>;

export interface ElectionYearData {
  electionDay: string;
  /** When Valmyndigheten fastställde the result (latest year only). */
  confirmedAt?: string;
  turnoutPct: number;
  districtsCounted?: number;
  /** National vote share (%), two decimals. */
  national: PartyShares;
  /** Combined share (%) of parties outside the riksdag: 100 minus the eight. */
  otherShare: number;
  seats: PartyShares;
  /** Vote share (%) per riksdagsvalkrets, keyed like build-geo-levels.mjs. */
  byRikskrets: Record<string, PartyShares>;
}

export interface RiksdagData {
  meta: { source: string; url: string; fetchedAt: string };
  latest: ElectionYear;
  previous: ElectionYear;
  years: Record<ElectionYear, ElectionYearData>;
}

export const RIKSDAG = riksdagJson as unknown as RiksdagData;

export const LATEST_YEAR: ElectionYear = '2026';
export const PREVIOUS_YEAR: ElectionYear = '2022';

/** Latest first, the order every surface lists them in. */
export const ELECTION_YEARS: ElectionYear[] = [LATEST_YEAR, PREVIOUS_YEAR];

export function election(year: ElectionYear): ElectionYearData {
  return RIKSDAG.years[year];
}

export function nationalShare(year: ElectionYear): PartyShares {
  return RIKSDAG.years[year].national;
}

export function rikskretsShare(year: ElectionYear, key: string): PartyShares | undefined {
  return RIKSDAG.years[year].byRikskrets[key];
}

export function seats(year: ElectionYear): PartyShares {
  return RIKSDAG.years[year].seats;
}

export function turnout(year: ElectionYear): number {
  return RIKSDAG.years[year].turnoutPct;
}

/** Signed change in national vote share (percentage points), two decimals. */
export function shareChange(
  party: PartyId,
  { from = PREVIOUS_YEAR, to = LATEST_YEAR }: { from?: ElectionYear; to?: ElectionYear } = {},
): number {
  return Math.round((nationalShare(to)[party] - nationalShare(from)[party]) * 100) / 100;
}

/**
 * A percentage-point change the Swedish way: "+0,8", "−2,3" (real minus sign),
 * or "±0" when it rounds to nothing at the displayed precision.
 */
export function formatChange(delta: number, decimals = 1): string {
  const factor = 10 ** decimals;
  const rounded = Math.round(Math.abs(delta) * factor) / factor;
  if (rounded === 0) return '±0';
  const sign = delta > 0 ? '+' : '−';
  return `${sign}${rounded.toFixed(decimals).replace('.', ',')}`;
}

/**
 * The line under a standings bar: the latest share and its change since the
 * previous election. Only the first row says "sedan 2022"; later rows carry the
 * bare delta so eight rows do not repeat the same words.
 * "28,0 % (−2,3 sedan 2022)" / "19,9 % (+0,8)".
 */
export function standingsNote(party: PartyId, { withYears }: { withYears: boolean }): string {
  const share = `${nationalShare(LATEST_YEAR)[party].toFixed(1).replace('.', ',')} %`;
  const change = formatChange(shareChange(party));
  return withYears ? `${share} (${change} sedan ${PREVIOUS_YEAR})` : `${share} (${change})`;
}

// ── Map popups ────────────────────────────────────────────────────────────────

/** Year-keyed results as baked into the GeoJSON by build-geo-levels.mjs. */
export type ElectionsByYear = Partial<Record<ElectionYear, Partial<Record<PartyId, number>>>>;

export interface ElectionSectionData {
  title: string;
  results: Partial<Record<PartyId, number>>;
}

/** One section per year present, latest first: "Riksdagsvalet 2026 — Skåne läns södra valkrets". */
export function electionSections(
  elections: ElectionsByYear | null | undefined,
  rikskretsName?: string | null,
): ElectionSectionData[] {
  if (!elections) return [];
  return ELECTION_YEARS.flatMap((year) => {
    const results = elections[year];
    if (!results) return [];
    const title = rikskretsName ? `Riksdagsvalet ${year} — ${rikskretsName}` : `Riksdagsvalet ${year}`;
    return [{ title, results }];
  });
}

/**
 * A county's result per year as the plain mean of its rikskretsar. Unweighted,
 * so a county split into constituencies of different size (Stockholm, Skåne,
 * Västra Götaland) is skewed toward the smaller ones. Known, left as is.
 */
export function countyElectionSections(
  rikskretsar: Array<{ elections?: ElectionsByYear | null }> | null | undefined,
): ElectionSectionData[] {
  if (!rikskretsar || rikskretsar.length === 0) return [];
  return ELECTION_YEARS.flatMap((year) => {
    const perKrets = rikskretsar.map((rk) => rk.elections?.[year]).filter((r) => r !== undefined);
    if (perKrets.length === 0) return [];
    const results: Partial<Record<PartyId, number>> = {};
    for (const p of PARTY_ORDER) {
      results[p] = perKrets.reduce((sum, r) => sum + (r[p] ?? 0), 0) / perKrets.length;
    }
    return [{ title: `Riksdagsvalet ${year}`, results }];
  });
}
